import PropTypes from 'prop-types';
import {useEffect, useState} from 'react';
import {useHistory} from 'react-router';
import {Table, Icon, Popup, Modal, Button, Image, Dimmer, Loader, Message} from 'semantic-ui-react';

import {useHttpClient} from 'hooks';

import Matomo from '../Common/Matomo';

import ShortUrlManagementModal from './ShortUrlManagementModal';

import styles from './ShortUrlsList.module.scss';

function QrCodeModal({url, onClose}) {
  const redirectorHost = new URL(url.url).host;
  const [blobUrl, setBlobUrl] = useState(null);
  const {data, error, loading} = useHttpClient({
    url: `/api/web-redirector/${redirectorHost}/short-urls/${url.id}/qr`,
    method: 'GET',
    responseType: 'blob',
  });

  useEffect(() => {
    if (!loading && !error) {
      setBlobUrl(URL.createObjectURL(data));
    }
  }, [data, loading, error, setBlobUrl]);

  const renderContent = () => {
    if (loading) {
      return (
        <Dimmer active inverted>
          <Loader>Loading QR code</Loader>
        </Dimmer>
      );
    } else if (error) {
      return (
        <Message negative>
          <Message.Header>There was a problem generating the QR code</Message.Header>
          <p>
            Please try again or open a{' '}
            <a
              href="https://cern.service-now.com/service-portal?id=service_element&name=web-portal"
              target="_blank"
              rel="noreferrer"
            >
              ticket
            </a>
          </p>
        </Message>
      );
    } else if (blobUrl) {
      return <Image size="medium" src={blobUrl} alt="qr_code" />;
    } else {
      return null;
    }
  };

  return (
    <Modal className={styles.QRCodeModal} size="small" onClose={onClose} closeIcon open>
      <Modal.Header>
        QR Code for <a href={url.url}>{url.url}</a>
      </Modal.Header>
      <Modal.Content image>{renderContent()}</Modal.Content>
      {blobUrl && (
        <Modal.Actions>
          <Button
            as="a"
            href={blobUrl}
            download={`qr-code-${url.slug}.png`}
            content="Download"
            labelPosition="right"
            icon="download"
            positive
          />
        </Modal.Actions>
      )}
    </Modal>
  );
}

QrCodeModal.propTypes = {
  url: PropTypes.shape({
    id: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

const ShortUrlsListItem = ({shortUrl: url}) => {
  const [openShortUrlManagementModal, setOpenShortUrlManagementModal] = useState(false);
  const [openQrCodeModal, setOpenQrCodeModal] = useState(false);
  const [openAnalyticsModal, setOpenAnalyticsModal] = useState(false);
  const history = useHistory();

  return (
    <Table.Row key={url.id}>
      <Table.Cell>
        <a href={url.url} target="_blank" rel="noreferrer">
          {url.url}
        </a>
      </Table.Cell>
      <Table.Cell>
        <Popup
          content={
            <div>
              <b>Description:</b>
              <br />
              {url.description}
            </div>
          }
          trigger={
            <a href={url.targetUrl} target="_blank" rel="noreferrer">
              {url.targetUrl}
            </a>
          }
          disabled={!url.description}
        />
      </Table.Cell>
      <Table.Cell textAlign="center">
        {openShortUrlManagementModal && (
          <ShortUrlManagementModal
            url={url}
            onUpdate={() => {
              setOpenShortUrlManagementModal(false);
            }}
            onClose={() => {
              setOpenShortUrlManagementModal(false);
            }}
            open
          />
        )}
        <Popup
          trigger={
            <Icon
              className={styles.Actions}
              name="qrcode"
              onClick={() => {
                setOpenQrCodeModal(true);
              }}
            />
          }
          position="top center"
        >
          QR code
        </Popup>
        {openQrCodeModal && <QrCodeModal onClose={() => setOpenQrCodeModal(false)} url={url} />}
        <Icon
          className={styles.Actions}
          name="line graph"
          onClick={() => setOpenAnalyticsModal(true)}
        />
        {openAnalyticsModal && (
          <Modal
            size="small"
            closeIcon
            open
            onClose={() => {
              setOpenAnalyticsModal(false);
              if (!url.matomoId) {
                history.push('/short-urls');
              }
            }}
          >
            <Modal.Header>Web Analytics</Modal.Header>
            <Modal.Content>
              <Matomo
                key={url.id}
                projectType="shorturl"
                projectName={url.id}
                siteUrl={url.url}
                siteId={Number(url.matomoId)}
              />
            </Modal.Content>
          </Modal>
        )}
        <Icon
          className={styles.Actions}
          name="setting"
          onClick={() => setOpenShortUrlManagementModal(true)}
        />
      </Table.Cell>
    </Table.Row>
  );
};

ShortUrlsListItem.propTypes = {
  shortUrl: PropTypes.object.isRequired,
};

export default ShortUrlsListItem;
